import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './FishInfo.css';
import logo from "../../assets/fishtagged-logo.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFish, faRuler, faBookmark, faAddressCard, faCommentDots, faAngleDown, faAngleLeft, faCircleQuestion, faAngleRight, faTimes, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import Hamburger from '../components/Hamburger';
import { loadImages } from "../../imageDB";
import { snapshotCurrentReport, upsertReport } from '../../reportStorage';

const FALLBACK_FISH_SPECIES = [
  "Smallmouth Bass","Largemouth Bass","Striped Bass","Walleye",
  "Northern Pike","Muskellunge","Rainbow Trout","Brown Trout",
  "Brook Trout","Lake Trout","Chinook Salmon","Coho Salmon",
  "Channel Catfish","Bluegill","Crappie","Yellow Perch","Carp","Other"
];
const FALLBACK_TAG_COLORS   = ["Red", "Green", "Orange", "Yellow", "Yellow-Green", "White", "Black", "Purple"];
const FALLBACK_TAG_PREFIXES = ["A","B","C","D","E","F","G","H"];

const WHOLE_INCHES = Array.from({length:60},(_,i)=>String(i+1));
const FRACTIONS    = ["0/4","1/4","1/2","3/4"];
const UNITS        = ["in","cm"];

function getOptions(types, typeName, fallback) {
  try {
    const t = types && types[typeName];
    if (t && Array.isArray(t.allowed_options) && t.allowed_options.length > 0) {
      return t.allowed_options;
    }
  } catch {}
  return fallback;
}

const Sel = ({ value, onChange, options, placeholder = "--", className = "" }) => (
  <div className={`fi-select-wrap ${className}`}>
    <select value={value} onChange={e => onChange(e.target.value)}>
      <option value="" disabled>{placeholder}</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
    <span className="fi-chevron"><FontAwesomeIcon icon={faAngleDown} /></span>
  </div>
);

export default function FishInfoScreen({ user, types = {} }) {
  const FISH_SPECIES = getOptions(types, "tagapp.species",    FALLBACK_FISH_SPECIES);
  const TAG_COLORS   = getOptions(types, "tagapp.tag_colors",  FALLBACK_TAG_COLORS);
  const TAG_PREFIXES = getOptions(types, "tagapp.tag_letter", FALLBACK_TAG_PREFIXES);
  const navigate = useNavigate();

  // Initialise state directly from sessionStorage so values are available
  // on the very first render — no restore useEffect needed.
  const [species,   setSpecies]   = useState(() => { try { return JSON.parse(sessionStorage.getItem("fishInfoData"))?.species   || ""; } catch { return ""; } });
  const [lenWhole,  setLenWhole]  = useState(() => { try { return JSON.parse(sessionStorage.getItem("fishInfoData"))?.lenWhole  || ""; } catch { return ""; } });
  const [lenFrac,   setLenFrac]   = useState(() => { try { return JSON.parse(sessionStorage.getItem("fishInfoData"))?.lenFrac   ?? "0"; } catch { return "0/4"; } });
  const [lenUnit,   setLenUnit]   = useState(() => { try { return JSON.parse(sessionStorage.getItem("fishInfoData"))?.lenUnit   || "in"; } catch { return "in"; } });
  const [tagColor,  setTagColor]  = useState(() => { try { return JSON.parse(sessionStorage.getItem("fishInfoData"))?.tagColor  || "RED"; } catch { return "RED"; } });
  const [tagPrefix, setTagPrefix] = useState(() => { try { return JSON.parse(sessionStorage.getItem("fishInfoData"))?.tagPrefix || "A"; } catch { return "A"; } });
  const [tagNumber, setTagNumber] = useState(() => { try { return JSON.parse(sessionStorage.getItem("fishInfoData"))?.tagNumber || ""; } catch { return ""; } });
  const [remarks,   setRemarks]   = useState(() => { try { return JSON.parse(sessionStorage.getItem("fishInfoData"))?.remarks  || ""; } catch { return ""; } });

  const [showValidationPopup, setShowValidationPopup] = useState(false);
  const [validationError,     setValidationError]     = useState("");

  // Auto-save on every field change
  useEffect(() => {
    const data = { species, lenWhole, lenFrac, lenUnit, tagColor, tagPrefix, tagNumber: tagNumber.trim(), remarks };
    sessionStorage.setItem("fishInfoData", JSON.stringify(data));

    const activeId = sessionStorage.getItem("activeReportId") || undefined;
    loadImages().then((imgs) => {
      const report = snapshotCurrentReport(activeId, imgs || []);
      upsertReport(report);
      if (!sessionStorage.getItem("activeReportId")) {
        sessionStorage.setItem("activeReportId", report.id);
      }
    });
  }, [species, lenWhole, lenFrac, lenUnit, tagColor, tagPrefix, tagNumber, remarks]);

  const handleNext = () => {
    const missing = [];
    if (!species)   missing.push("Fish Species");
    if (!lenWhole)  missing.push("Fish Length (whole number)");
    if (!lenUnit)   missing.push("Fish Length (unit)");
    if (!tagColor)  missing.push("Tag Color");
    if (!tagPrefix) missing.push("Tag Prefix");
    if (!tagNumber.trim()) {
      missing.push("Tag Number");
    } else if (!/^\d{2,4}$/.test(tagNumber.trim())) {
      missing.push("Tag Number must be 2–4 digits");
    }
    if (missing.length > 0) {
      setValidationError(`Please fill in the following before continuing:\n• ${missing.join("\n• ")}`);
      setShowValidationPopup(true);
      return;
    }
    navigate("/finalreview");
  };

  return (
    <>
      <div className="bg-image" />
      <div className="top-row">
        <div className="logo"><img src={logo} alt="FishTagged Logo" /></div>
        <div className="top-hamburger"><Hamburger user={user} /></div>
      </div>
      <div className="ri-viewport">
        <div className="ri-content">
          <div className="card-wrapper">
            <main className="fi-card">
              <p className="fi-step-label">New entry (4 out of 5)</p>
              <h1 className="fi-title">Fish Information</h1>
              <p className="fi-subtitle">Please enter details about the fish.</p>
              <div className="fi-form-box">
                <div className="fi-field-row">
                  <span className="fi-field-icon"><FontAwesomeIcon icon={faFish} /></span>
                  <div className="fi-field-info"><span className="fi-field-label">Fish Species *</span></div>
                  <Sel value={species} onChange={setSpecies} options={FISH_SPECIES} className="fi-select-wide" />
                </div>
                <div className="fi-field-row">
                  <span className="fi-field-icon"><FontAwesomeIcon icon={faRuler} /></span>
                  <div className="fi-field-info">
                    <span className="fi-field-label">Fish Length *</span>
                    <span className="fi-field-value">Greater than 0</span>
                  </div>
                  <div className="fi-length-group">
                    <Sel value={lenWhole} onChange={setLenWhole} options={WHOLE_INCHES} placeholder="--" className="fi-select-sm" />
                    <Sel value={lenFrac}  onChange={setLenFrac}  options={FRACTIONS}    placeholder="--" className="fi-select-sm" />
                    <Sel value={lenUnit}  onChange={setLenUnit}  options={UNITS}        placeholder="--" className="fi-select-xs" />
                  </div>
                </div>
                <div className="fi-field-row">
                  <span className="fi-field-icon"><FontAwesomeIcon icon={faBookmark} /></span>
                  <div className="fi-field-info"><span className="fi-field-label">Tag Color *</span></div>
                  <Sel value={tagColor} onChange={setTagColor} options={TAG_COLORS} className="fi-select-med" />
                </div>
                <div className="fi-field-row fi-field-row--wrap">
                  <span className="fi-field-icon"><FontAwesomeIcon icon={faAddressCard} /></span>
                  <div className="fi-field-info"><span className="fi-field-label">Tag ID *</span></div>
                  <div className="fi-tagid-group">
                    <Sel value={tagPrefix} onChange={setTagPrefix} options={TAG_PREFIXES} placeholder="--" className="fi-select-xs" />
                    <input className="fi-tag-number" type="text" value={tagNumber} onChange={e => setTagNumber(e.target.value)} maxLength={4} placeholder="9999" />
                  </div>
                  <p className="fi-field-subhint">Tag numbers must be 2-4 digits. Enter tag letter and number separately.</p>
                </div>
                <div className="fi-field-row fi-field-row--remarks fi-field-row--last">
                  <span className="fi-field-icon"><FontAwesomeIcon icon={faCommentDots} /></span>
                  <div className="fi-field-info">
                    <span className="fi-field-label">Remarks <span style={{fontWeight:400, fontSize:'0.85em', color:'#999'}}>(Optional)</span></span>
                  </div>
                  <div className="fi-tooltip-wrap">
                    <button className="fi-help-btn" aria-label="Help"><FontAwesomeIcon icon={faCircleQuestion} /></button>
                    <div className="fi-tooltip">##</div>
                  </div>
                  <textarea className="fi-remarks" value={remarks} onChange={e => setRemarks(e.target.value)} rows={4} />
                </div>
              </div>
            </main>
            <div className="bottom-nav">
              <FontAwesomeIcon icon={faAngleLeft} className="arrow" onClick={() => navigate("/reportImage")} />
              <FontAwesomeIcon icon={faAngleRight} className="arrow" onClick={handleNext} />
            </div>
          </div>
        </div>
      </div>
      {showValidationPopup && (
        <div className="ri-modal-overlay">
          <div className="ri-modal">
            <FontAwesomeIcon icon={faTimes} className="ri-modal-close" onClick={() => setShowValidationPopup(false)} />
            <FontAwesomeIcon icon={faExclamationTriangle} className="ri-warning-icon" />
            <h2 className="ri-modal-title">Missing Information</h2>
            <p className="ri-modal-body" style={{ whiteSpace: 'pre-line' }}>{validationError}</p>
            <div className="ri-modal-actions">
              <button className="ri-modal-choose" onClick={() => setShowValidationPopup(false)}>OK</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}